import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { DollarSign, Upload, Search } from 'lucide-react';
import { AdminPageHeader, AdminPageShell, AdminPageBody } from '../../components/AdminPageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const cpfFmt = (c) => {
  const d = (c || '').replace(/\D/g, '').padStart(11, '0').slice(-11);
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
};

const AdminCommissionPayments = () => {
  const navigate = useNavigate();
  const [partnerCpf, setPartnerCpf] = useState('');
  const [pixData, setPixData] = useState(null);
  const [commissions, setCommissions] = useState(null); // { commissions: [], summary: {} }
  const [selected, setSelected] = useState({}); // reservation_id → boolean
  const [msg, setMsg] = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) navigate('/admin/login');
  }, [navigate]);

  const search = async () => {
    const cpf = partnerCpf.replace(/\D/g, '');
    if (cpf.length !== 11) { setMsg({ type: 'error', text: 'CPF inválido' }); return; }
    try {
      const [pix, com, pays] = await Promise.all([
        axios.get(`${API_URL}/api/commission-payments/partner-pix/${cpf}`),
        axios.get(`${API_URL}/api/partner/commissions/${cpf}`),
        axios.get(`${API_URL}/api/commission-payments/partner/${cpf}`),
      ]);
      setPixData(pix.data);
      setCommissions(com.data);
      setPayments(pays.data.payments || []);
      setSelected({});
      setMsg(null);
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erro' }); }
  };

  const eligible = (commissions?.commissions || []).filter(c => c.status === 'available' && !c.commission_paid_at);
  const totalSelected = eligible.filter(c => selected[c.reservation_id]).reduce((sum, c) => sum + (c.commission_value || 0), 0);

  const createPayment = async () => {
    const ids = eligible.filter(c => selected[c.reservation_id]).map(c => c.reservation_id);
    if (!ids.length) { setMsg({ type: 'error', text: 'Selecione ao menos uma comissão.' }); return; }
    try {
      const r = await axios.post(`${API_URL}/api/commission-payments/create`, {
        partner_cpf: partnerCpf.replace(/\D/g, ''),
        reservation_ids: ids,
        amount: totalSelected,
      });
      setMsg({ type: 'success', text: `Pagamento criado (${r.data.payment_id}). Anexe o comprovante abaixo.` });
      search();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erro ao criar.' }); }
  };

  const attachProof = async (paymentId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API_URL}/api/commission-payments/attach-proof`, {
          payment_id: paymentId,
          file_base64: reader.result,
          filename: file.name,
        });
        setMsg({ type: 'success', text: 'Comprovante anexado. Status: Recebido.' });
        search();
      } catch (e) { setMsg({ type: 'error', text: 'Erro ao anexar.' }); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Pagamentos de Comissão"
        description="Selecionar comissões elegíveis, criar pagamento e anexar comprovante PIX"
        Icon={DollarSign}
      />

      <AdminPageBody>
        {msg && <div className={`p-3 rounded ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{msg.text}</div>}

        <Card>
          <CardContent className="p-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label>CPF do Parceiro</Label>
                <Input data-testid="cp-search-cpf" value={partnerCpf} onChange={(e) => setPartnerCpf(e.target.value)} placeholder="000.000.000-00" />
              </div>
              <Button onClick={search} data-testid="cp-search-btn"><Search className="w-4 h-4 mr-1" /> Buscar</Button>
            </div>
          </CardContent>
        </Card>

        {pixData && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold mb-2">Dados de Recebimento</h3>
              {pixData.has_pix ? (
                <div className="grid grid-cols-2 gap-2 text-sm" data-testid="cp-pix-data">
                  <p><strong>Chave PIX:</strong> {pixData.pix_key}</p>
                  <p><strong>Tipo:</strong> {pixData.pix_key_type}</p>
                  <p><strong>Titular:</strong> {pixData.pix_holder_name}</p>
                  <p><strong>CPF/CNPJ:</strong> {pixData.pix_holder_document}</p>
                </div>
              ) : (
                <p className="text-sm text-amber-700">Parceiro ainda não cadastrou chave PIX.</p>
              )}
            </CardContent>
          </Card>
        )}

        {commissions && (
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between mb-3">
                <h3 className="font-bold">Comissões elegíveis a pagamento</h3>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Selecionado</p>
                  <p className="text-xl font-bold text-emerald-700" data-testid="cp-total-selected">{brl(totalSelected)}</p>
                </div>
              </div>
              {eligible.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Nenhuma comissão disponível para pagamento.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2">✓</th>
                        <th className="p-2 text-left">Cliente</th>
                        <th className="p-2 text-left">Pacote</th>
                        <th className="p-2 text-left">Data Viagem</th>
                        <th className="p-2 text-left">Cupom</th>
                        <th className="p-2 text-right">Comissão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligible.map(c => (
                        <tr key={c.reservation_id} className="border-b hover:bg-gray-50">
                          <td className="p-2 text-center">
                            <input type="checkbox" data-testid={`cp-sel-${c.reservation_id}`}
                              checked={!!selected[c.reservation_id]}
                              onChange={(e) => setSelected(s => ({ ...s, [c.reservation_id]: e.target.checked }))} />
                          </td>
                          <td className="p-2 uppercase">{c.customer_name}</td>
                          <td className="p-2">{c.package_name}</td>
                          <td className="p-2">{c.selected_date}</td>
                          <td className="p-2 font-mono text-xs">{c.coupon_code}</td>
                          <td className="p-2 text-right font-bold text-emerald-700">{brl(c.commission_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Button className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={createPayment} disabled={totalSelected <= 0} data-testid="cp-create-btn">
                Criar Pagamento ({brl(totalSelected)})
              </Button>
            </CardContent>
          </Card>
        )}

        {payments.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold mb-3">Histórico de Pagamentos</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">Data</th>
                    <th className="p-2 text-right">Valor</th>
                    <th className="p-2 text-center">Clientes</th>
                    <th className="p-2 text-center">Status</th>
                    <th className="p-2 text-center">Comprovante</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} className="border-b" data-testid={`cp-payment-${p.id}`}>
                      <td className="p-2 text-xs">{new Date(p.created_at).toLocaleString('pt-BR')}</td>
                      <td className="p-2 text-right font-bold">{brl(p.amount)}</td>
                      <td className="p-2 text-center">{(p.details || []).length}</td>
                      <td className="p-2 text-center">
                        <Badge className={p.status === 'received' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                          {p.status_label}
                        </Badge>
                      </td>
                      <td className="p-2 text-center">
                        {p.status === 'received' ? (
                          <span className="text-xs text-emerald-700">✓ {p.proof_file?.filename}</span>
                        ) : (
                          <label className="cursor-pointer text-xs text-blue-700 hover:underline">
                            <Upload className="w-3 h-3 inline mr-1" /> Anexar
                            <input type="file" className="hidden" data-testid={`cp-upload-${p.id}`}
                              onChange={(e) => attachProof(p.id, e.target.files?.[0])} />
                          </label>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </AdminPageBody>
    </AdminPageShell>
  );
};

export default AdminCommissionPayments;
